import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import {
  HiXMark,
  HiCheckCircle,
  HiExclamationCircle,
  HiInformationCircle,
  HiBell,
  HiChatBubbleLeftRight
} from 'react-icons/hi2';

export type ToastType = 'success' | 'error' | 'info' | 'warning' | 'kodi' | 'alert';

export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number; // ms, 0 = no auto-dismiss
  onClick?: () => void;
}

interface ToastContextValue {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => string;
  removeToast: (id: string) => void;
  clearToasts: () => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: React.ReactNode;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
}

export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  position = 'bottom-right'
}) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((toast: Omit<Toast, 'id'>): string => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newToast: Toast = {
      ...toast,
      id,
      duration: toast.duration ?? 5000 // Default 5 seconds
    };

    setToasts((prev) => [...prev, newToast]);
    return id;
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const clearToasts = useCallback(() => {
    setToasts([]);
  }, []);

  // Position classes
  const positionClasses = {
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4'
  };

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast, clearToasts }}>
      {children}

      {/* Toast Container */}
      <div
        className={`fixed ${positionClasses[position]} z-[100] flex flex-col gap-3 pointer-events-none`}
        style={{ maxWidth: '420px' }}
      >
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={() => removeToast(toast.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

interface ToastItemProps {
  toast: Toast;
  onDismiss: () => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onDismiss }) => {
  const [isExiting, setIsExiting] = useState(false);

  // Handle dismiss with fade-out animation
  const handleDismiss = useCallback(() => {
    setIsExiting(true);
    // Wait for animation to complete before actually removing
    setTimeout(onDismiss, 300);
  }, [onDismiss]);

  // Auto-dismiss timer
  useEffect(() => {
    if (toast.duration && toast.duration > 0) {
      const timer = setTimeout(handleDismiss, toast.duration);
      return () => clearTimeout(timer);
    }
  }, [toast.duration, handleDismiss]);

  // Icon and colors based on type
  const getTypeStyles = () => {
    switch (toast.type) {
      case 'success':
        return {
          icon: <HiCheckCircle className="w-5 h-5" />,
          bgClass: 'bg-vb-success/10 border-vb-success/30',
          iconClass: 'text-vb-success',
          titleClass: 'text-vb-success'
        };
      case 'error':
        return {
          icon: <HiExclamationCircle className="w-5 h-5" />,
          bgClass: 'bg-vb-danger/10 border-vb-danger/30',
          iconClass: 'text-vb-danger',
          titleClass: 'text-vb-danger'
        };
      case 'warning':
        return {
          icon: <HiExclamationCircle className="w-5 h-5" />,
          bgClass: 'bg-vb-warning/10 border-vb-warning/30',
          iconClass: 'text-vb-warning',
          titleClass: 'text-vb-warning'
        };
      case 'kodi':
        return {
          icon: <HiChatBubbleLeftRight className="w-5 h-5" />,
          bgClass: 'bg-vb-primary/10 border-vb-primary/30',
          iconClass: 'text-vb-primary',
          titleClass: 'text-vb-primary'
        };
      case 'alert':
        return {
          icon: <HiBell className="w-5 h-5" />,
          bgClass: 'bg-vb-warning/10 border-vb-warning/30',
          iconClass: 'text-vb-warning',
          titleClass: 'text-vb-warning'
        };
      case 'info':
      default:
        return {
          icon: <HiInformationCircle className="w-5 h-5" />,
          bgClass: 'bg-vb-primary/10 border-vb-primary/30',
          iconClass: 'text-vb-primary',
          titleClass: 'text-vb-primary'
        };
    }
  };

  const styles = getTypeStyles();
  const isClickable = !!toast.onClick;

  // Handle click - dismiss then call onClick
  const handleClick = () => {
    if (toast.onClick) {
      handleDismiss();
      toast.onClick();
    }
  };

  return (
    <div
      className={`
        pointer-events-auto 
        ${styles.bgClass} 
        border 
        rounded-lg 
        shadow-lg 
        backdrop-blur-sm
        ${isExiting ? 'animate-slide-out-right' : 'animate-slide-in-right'}
        ${isClickable ? 'cursor-pointer hover:scale-[1.02] transition-transform' : ''}
      `}
      onClick={handleClick}
      role={isClickable ? 'button' : undefined}
    >
      <div className="flex items-start gap-3 p-4">
        {/* Icon */}
        <div className={`flex-shrink-0 ${styles.iconClass}`}>{styles.icon}</div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className={`font-semibold text-sm ${styles.titleClass}`}>{toast.title}</p>
          {toast.message && (
            <p className="text-sm text-vb-text-secondary dark:text-vb-text-secondary-dark mt-1 line-clamp-2">
              {toast.message}
            </p>
          )}
        </div>

        {/* Dismiss button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleDismiss();
          }}
          aria-label="Dismiss notification"
          className="flex-shrink-0 p-1 rounded hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
        >
          <HiXMark className="w-4 h-4 text-vb-text-secondary dark:text-vb-text-secondary-dark" />
        </button>
      </div>
    </div>
  );
};

// Add animation to global styles or Tailwind config
// For now, we'll use inline keyframes
const style = document.createElement('style');
style.textContent = `
  @keyframes slide-in-right {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  @keyframes slide-out-right {
    from {
      transform: translateX(0);
      opacity: 1;
    }
    to {
      transform: translateX(100%);
      opacity: 0;
    }
  }
  .animate-slide-in-right {
    animation: slide-in-right 0.3s ease-out forwards;
  }
  .animate-slide-out-right {
    animation: slide-out-right 0.3s ease-in forwards;
  }
`;
if (typeof document !== 'undefined' && !document.getElementById('toast-animations')) {
  style.id = 'toast-animations';
  document.head.appendChild(style);
}
